import {
  Accordion,
  AccordionItem,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
} from "@heroui/react";
import { Database, Home, KeyRound } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { logout } from "@/lib/auth";
import { useAuth } from "@/lib/auth-context";
import { canWrite } from "@/lib/authorization";
import { MASTER_NAV_ITEMS, NAV_ITEMS } from "@/lib/nav";

/**
 * アプリ共通のドロワー。
 *
 * ngtg_msc の HomeDrawer に準拠し、ブランドカラーのヘッダ（アプリ名・ユーザー名・
 * ログアウト）に続けてメニューを並べる。現在地はブランドカラーでハイライトし、
 * マスタは折りたたみにする。
 */
export default function AppDrawer({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { user, clear } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const goTo = (path: string) => {
    onClose();
    navigate(path);
  };

  // 現在地の判定。マスタ配下は前方一致で見る（折りたたみの初期展開にも使う）。
  const isCurrent = (path: string) => location.pathname === path;
  const inMaster = location.pathname.startsWith("/master");

  const itemClass = (active: boolean) =>
    [
      "flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm transition-colors",
      active
        ? "bg-brand-50 font-bold text-brand-500"
        : "text-default-700 hover:bg-default-100",
    ].join(" ");

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      placement="left"
      size="xs"
      classNames={{ closeButton: "text-white z-10" }}
    >
      <DrawerContent>
        <DrawerHeader className="flex-col items-start gap-3 bg-brand-500 pb-4">
          <span className="text-2xl font-black text-white">BOMIEL</span>
          {user && (
            <span className="text-sm text-white">{user.name ?? user.email} さん</span>
          )}
          <Button
            size="sm"
            className="self-end bg-white font-medium text-brand-500"
            onPress={() => {
              onClose();
              void logout().finally(() => {
                clear();
                navigate("/login", { replace: true });
              });
            }}
          >
            ログアウト
          </Button>
        </DrawerHeader>

        <DrawerBody className="gap-1 px-2 py-3">
          <button
            type="button"
            className={itemClass(isCurrent("/"))}
            onClick={() => goTo("/")}
          >
            <Home size={20} />
            ホーム
          </button>

          {NAV_ITEMS.map((item) => (
            <button
              key={item.path}
              type="button"
              className={itemClass(isCurrent(item.path))}
              onClick={() => goTo(item.path)}
            >
              <item.icon size={20} />
              {item.label}
            </button>
          ))}

          {/* マスタ（親セクション）。現在地がマスタ配下なら開いた状態にする。 */}
          <Accordion
            defaultExpandedKeys={inMaster ? ["master"] : []}
            className="px-0"
            // 左端とホバーの見え方を他の項目（itemClass）にそろえる。
            // 既定のトリガーには横の余白が無く、マスタだけ左にずれていた。
            itemClasses={{
              trigger: "rounded-md px-3 py-2.5 hover:bg-default-100",
              content: "pb-0 pl-4",
            }}
          >
            <AccordionItem
              key="master"
              aria-label="マスタ"
              title={
                <span className="flex items-center gap-3 text-sm text-default-700">
                  <Database size={20} />
                  マスタ
                </span>
              }
            >
              {/* ユーザー管理は管理者のみ表示（API 側でも write は ADMIN 限定）。 */}
              {MASTER_NAV_ITEMS.filter(
                (item) => !item.adminOnly || canWrite(user, "userAdmin"),
              ).map((item) => (
                <button
                  key={item.path}
                  type="button"
                  className={itemClass(isCurrent(item.path))}
                  onClick={() => goTo(item.path)}
                >
                  <item.icon size={18} />
                  {item.label}
                </button>
              ))}
            </AccordionItem>
          </Accordion>

          {/* パスワード変更は**引き出しの一番下**に貼り付ける。
              日常的に押すものではなく、画面を探すときの邪魔になってほしくない。
              `mt-auto` で、項目が少ないときも下端に落ちる（末尾に置くだけだと
              行が短い環境で中途半端な位置に浮く）。
              パスワードを忘れた場合は管理者による仮パスワードの再発行になる
              （メールを使わない運用のため）。自分で変えたいときはここから。 */}
          <div className="mt-auto border-t border-default-200 pt-2">
            <button
              type="button"
              className={itemClass(isCurrent("/password-change"))}
              onClick={() => goTo("/password-change")}
            >
              <KeyRound size={20} />
              パスワード変更
            </button>
          </div>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
